import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ClipboardCheck,
  ShieldAlert,
  Fuel,
  Anchor,
  FileCheck2,
  Search,
  Shield,
  Radio,
  Compass,
  Beaker,
  Timer,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useI18n } from '@/i18n/I18nContext';
import { SERVICES } from '@/constants/testIds';
import { SERVICE_SLUGS } from '@/lib/services';

const iconMap = {
  'condition-prepurchase': ClipboardCheck,
  damage: ShieldAlert,
  'draft-bunker': Fuel,
  'on-off-hire': Anchor,
  'ism-isps-mlc': FileCheck2,
  'pre-vetting-sire-cdi': Search,
  'psc-inspection': Shield,
  'remote-vdr': Radio,
  'navigation-audit': Compass,
  'blending-services': Beaker,
  'loss-control-expediting': Timer,
};

const ServicesPage = () => {
  const { t } = useI18n();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <main data-testid={SERVICES.detailRoot} className="bg-[#050D17]">
      {/* Hero of services page */}
      <section className="relative pt-36 pb-20 sm:pt-44 sm:pb-24 overflow-hidden">
        <div
          className="absolute inset-0 -z-20 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjb250YWluZXIlMjBzaGlwJTIwb2NlYW58ZW58MHx8fHwxNzgwOTAzNzQyfDA&ixlib=rb-4.1.0&q=85&w=2000&h=1200)',
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#050D17]/95 via-[#050D17]/95 to-[#050D17]" />

        <div className="container mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#C9A24E] hover:text-[#E6CC80] transition-colors"
          >
            <ArrowLeft size={14} />
            {t('servicesPage.backHome')}
          </Link>

          <div className="mt-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C9A24E]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]">
                {t('servicesPage.eyebrow')}
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F2F5F8] leading-[1.05]">
              {t('servicesPage.title')}
            </h1>
            <p className="mt-6 text-[15px] sm:text-base text-[#C9D2E2] leading-relaxed max-w-2xl">
              {t('servicesPage.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Service list */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="space-y-12">
            {SERVICE_SLUGS.map((slug, idx) => {
              const Icon = iconMap[slug] || ClipboardCheck;
              return (
                <article
                  key={slug}
                  id={slug}
                  data-testid={SERVICES.detailItem(slug)}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 pt-12 border-t border-[#C9A24E]/15 scroll-mt-28"
                >
                  <div className="md:col-span-3 flex md:flex-col items-start gap-4">
                    <div className="font-serif text-[#C9A24E] text-3xl leading-none">
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <div className="h-14 w-14 flex items-center justify-center border border-[#C9A24E]/40 text-[#C9A24E]">
                      <Icon size={22} strokeWidth={1.4} />
                    </div>
                  </div>
                  <div className="md:col-span-9">
                    <h2 className="font-serif text-2xl sm:text-3xl text-[#F2F5F8] leading-snug">
                      {t(`services.items.${slug}.name`)}
                    </h2>
                    <p className="mt-3 text-[#C9A24E] text-sm uppercase tracking-[0.22em]">
                      {t(`services.items.${slug}.short`)}
                    </p>
                    <p className="mt-5 text-[15px] text-[#C9D2E2] leading-relaxed max-w-3xl">
                      {t(`services.items.${slug}.long`)}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32">
        <div className="container mx-auto px-6">
          <div className="border border-[#C9A24E]/30 p-10 sm:p-14 bg-[#0A1A2F] flex flex-col md:flex-row gap-8 md:items-center md:justify-between">
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#F2F5F8] leading-tight max-w-xl">
                {t('servicesPage.ctaTitle')}
              </h3>
              <p className="mt-3 text-[#C9D2E2] text-sm max-w-xl">
                {t('servicesPage.ctaBody')}
              </p>
            </div>
            <Link
              to="/#contact"
              data-testid={SERVICES.ctaSchedule}
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#C9A24E] text-[#050D17] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E6CC80] transition-colors self-start"
            >
              {t('servicesPage.ctaButton')}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
